'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  LYRRA_A11Y_UPDATED_EVENT,
} from '@/hooks/useKeyboardNavigationSetting';
import {
  bindGlobalPointerHover,
  fetchImageHoverAudio,
  getImageBoundingRect,
  readImageHoverAudioEnabled,
  resolveImageHoverLoadingKind,
  type ImageHoverLoadingKind,
} from '@/lib/a11y-image-hover';

const Z_INDEX_RING = 9745;
const Z_INDEX_BADGE = 9746;

type OverlayState =
  | { status: 'idle' }
  | { status: 'loading'; rect: DOMRect; kind: ImageHoverLoadingKind }
  | { status: 'playing'; rect: DOMRect }
  | { status: 'error'; rect: DOMRect; message: string };

function loadingLabel(
  kind: ImageHoverLoadingKind,
  t: (key: string, fallback: string) => string,
): string {
  if (kind === 'vision') {
    return t(
      'accessibility.imageHoverAudio.loadingVision',
      'Analyse de l’image et génération audio…',
    );
  }
  if (kind === 'tts') {
    return t(
      'accessibility.imageHoverAudio.loadingTts',
      'Génération de la description audio…',
    );
  }
  return t(
    'accessibility.imageHoverAudio.loading',
    'Chargement de la description audio…',
  );
}

function ImageHoverAudioOverlay({
  overlay,
  t,
}: {
  overlay: Exclude<OverlayState, { status: 'idle' }>;
  t: (key: string, fallback: string) => string;
}) {
  const { rect } = overlay;
  const badgeTop = Math.min(window.innerHeight - 56, Math.max(8, rect.bottom + 8));
  const badgeLeft = Math.min(
    window.innerWidth - 16,
    Math.max(16, rect.left + rect.width / 2),
  );

  const isLoading = overlay.status === 'loading';
  const isPlaying = overlay.status === 'playing';
  const isError = overlay.status === 'error';

  let label = '';
  if (isLoading) label = loadingLabel(overlay.kind, t);
  else if (isPlaying) {
    label = t('accessibility.imageHoverAudio.playing', 'Lecture de la description…');
  } else if (isError) {
    label = overlay.message;
  }

  return createPortal(
    <>
      <div
        data-a11y-no-speak="true"
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: isError
            ? '0 0 0 3px rgba(239, 68, 68, 0.75)'
            : '0 0 0 3px rgba(59, 130, 246, 0.72)',
          borderRadius: 6,
          pointerEvents: 'none',
          zIndex: Z_INDEX_RING,
          transition: 'box-shadow 180ms ease',
        }}
      />
      <div
        data-a11y-no-speak="true"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'fixed',
          top: badgeTop,
          left: badgeLeft,
          transform: 'translateX(-50%)',
          zIndex: Z_INDEX_BADGE,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          maxWidth: Math.min(320, window.innerWidth - 24),
          padding: '8px 12px',
          borderRadius: 999,
          background: isError ? 'rgba(185, 28, 28, 0.96)' : 'rgba(0, 27, 61, 0.94)',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.1,
          boxShadow: '0 12px 28px rgba(0,0,0,0.32)',
          border: '1px solid rgba(255,255,255,0.14)',
          pointerEvents: 'none',
        }}
      >
        {isLoading ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
            style={{ animation: 'a11y-img-spin 0.9s linear infinite', flexShrink: 0 }}
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : isPlaying ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2A4.5 4.5 0 0 0 14 7.97v8.06A4.5 4.5 0 0 0 16.5 12z" />
          </svg>
        ) : null}
        <span>{label}</span>
      </div>
      <style>{`@keyframes a11y-img-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>,
    document.body,
  );
}

/**
 * Lecture audio automatique au survol de toutes les images (pages publiques,
 * privées, player podcast, landing shadow DOM…) lorsque l'option malvoyance est active.
 */
export function GlobalImageHoverAudio() {
  const { i18n, t } = useTranslation();
  const [enabled, setEnabled] = useState(readImageHoverAudioEnabled);
  const [overlay, setOverlay] = useState<OverlayState>({ status: 'idle' });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestGenRef = useRef(0);
  const activeImgRef = useRef<HTMLImageElement | null>(null);
  const errorTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const sync = () => setEnabled(readImageHoverAudioEnabled());
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const clearOverlay = useCallback(() => {
    if (errorTimerRef.current) {
      window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    setOverlay({ status: 'idle' });
  }, []);

  const stopPlayback = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    try {
      audioRef.current?.pause();
    } catch {
      /* ignore */
    }
    if (audioRef.current) audioRef.current.src = '';
    activeImgRef.current = null;
    clearOverlay();
  }, [clearOverlay]);

  const showError = useCallback(
    (img: HTMLImageElement, message: string) => {
      setOverlay({
        status: 'error',
        rect: getImageBoundingRect(img),
        message,
      });
      if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = window.setTimeout(() => {
        if (activeImgRef.current === img) activeImgRef.current = null;
        setOverlay({ status: 'idle' });
        errorTimerRef.current = null;
      }, 2200);
    },
    [],
  );

  const playForImage = useCallback(
    async (img: HTMLImageElement) => {
      const gen = ++requestGenRef.current;
      activeImgRef.current = img;

      const language = (
        i18n.resolvedLanguage ||
        i18n.language ||
        document.documentElement.lang ||
        'fr'
      ).toString();

      const kind = resolveImageHoverLoadingKind(img);
      if (kind === 'none') {
        showError(
          img,
          t(
            'accessibility.imageHoverAudio.unavailable',
            'Description audio indisponible pour cette image.',
          ),
        );
        return;
      }

      setOverlay({
        status: 'loading',
        rect: getImageBoundingRect(img),
        kind,
      });

      abortRef.current?.abort();
      try {
        audioRef.current?.pause();
      } catch {
        /* ignore */
      }
      if (audioRef.current) audioRef.current.src = '';

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const entry = await fetchImageHoverAudio(img, language);
        if (!entry || gen !== requestGenRef.current || controller.signal.aborted) return;
        if (activeImgRef.current !== img) return;

        const audio = audioRef.current ?? new Audio();
        audioRef.current = audio;
        audio.onended = () => {
          if (audioRef.current === audio && activeImgRef.current === img) {
            activeImgRef.current = null;
            clearOverlay();
          }
        };
        audio.onerror = () => {
          if (activeImgRef.current === img) {
            showError(
              img,
              t('accessibility.imageHoverAudio.error', 'Lecture audio impossible.'),
            );
          }
        };

        setOverlay({ status: 'playing', rect: getImageBoundingRect(img) });
        audio.src = entry.objectUrl;
        audio.currentTime = 0;
        await audio.play();
      } catch {
        if (gen === requestGenRef.current && activeImgRef.current === img) {
          showError(
            img,
            t('accessibility.imageHoverAudio.error', 'Lecture audio impossible.'),
          );
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [i18n.resolvedLanguage, i18n.language, t, showError, clearOverlay],
  );

  useEffect(() => {
    if (!enabled) {
      stopPlayback();
      return;
    }

    return bindGlobalPointerHover(
      () => readImageHoverAudioEnabled(),
      (img) => {
        void playForImage(img);
      },
      () => {
        stopPlayback();
      },
    );
  }, [enabled, playForImage, stopPlayback]);

  useEffect(() => {
    if (overlay.status === 'idle') return;

    const syncRect = () => {
      const img = activeImgRef.current;
      if (!img) return;
      const rect = getImageBoundingRect(img);
      setOverlay((current) =>
        current.status === 'idle' ? current : { ...current, rect },
      );
    };

    window.addEventListener('scroll', syncRect, true);
    window.addEventListener('resize', syncRect);
    return () => {
      window.removeEventListener('scroll', syncRect, true);
      window.removeEventListener('resize', syncRect);
    };
  }, [overlay.status]);

  useEffect(
    () => () => {
      stopPlayback();
    },
    [stopPlayback],
  );

  if (typeof document === 'undefined' || overlay.status === 'idle') return null;

  return <ImageHoverAudioOverlay overlay={overlay} t={t} />;
}

export default GlobalImageHoverAudio;
